/* eslint-disable react/no-unknown-property */
import React, { FormEventHandler, useEffect } from 'react';
import { Button, Grid, Typography, Box as MUIBox } from '@material-ui/core';
import {
  Field,
  FieldArray,
  formValueSelector,
  getFormSyncErrors,
  getFormValues,
  initialize,
  reduxForm,
  stopSubmit,
} from 'redux-form/immutable';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import Alert from '@mui/material/Alert';
import { InfoOutlined } from '@mui/icons-material';
import { makeStyles } from '@material-ui/core/styles';

import Box from 'retired/Box';
import FormLocationField from 'retired/FormLocationField';
import FormTextField from 'retired/FormTextField';
import FormFieldLabel from 'retired/FormFieldLabel';
import FormValueSelector from 'retired/FormValueSelector';
import { required, notEmpty, isAddress, maxLength } from 'utils/validators';
import { selectCategories, selectSDGs, selectPillars, selectERGs } from 'entities/cause/selectors';
import type { State } from 'reducers';
import countries from 'utils/countries';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import { FormAddressValidationField } from 'components/FormAddressValidationField/FormAddressValidationField';
import createTextMask from 'components/MaskInput/createTextMask';
import FormCheckbox from 'retired/FormCheckbox';
import { Privileges } from 'utils/privileges';
import { TypedMap } from 'utils/typed-map';
import { showErrorAction } from 'containers/Notification/actions';
import Organization from 'entities/organization/model';
import { NonprofitAddressChangeRequestStatus } from 'generated/graphql';
import { renderAddress } from 'utils/renderAddress';

import * as Actions from './actions';
import { selectNewAddress } from './selectors';
import { RequestNonprofitAddressChange } from './RequestNonprofitAddressChange';

export const FORM_IDENTIFIER = 'app/OrganizationPage/PresenceForm';

const useStyles = makeStyles({ message: { flexGrow: 1 } });

const requiredValidator = required();
const isAddressValidator = isAddress();
const notEmptyValidator = notEmpty();
const maxLength5Validator = maxLength(5);

// @TODO-VZ: Move regexps an error messages for facebook and instagram to shared
const facebookUrlValidator = (value: string) =>
  !value ||
  /^(?:https?:\/\/)?(?:www\.)?(?:facebook|fb|m\.facebook)\.(?:com|me)\/(?:(?:\w)*#!\/)?(?:pages\/)?(?:[\w?=&-]*\/)*([\w?=&\-.]+)(?:\/)?$/i.test(
    value.trim()
  )
    ? undefined
    : 'Value should be a complete facebook link starting from https://... If you would prefer not to set a facebook link, clear the field to continue.';

const instagramHandleValidator = (value: string) =>
  !value || /^[a-zA-Z0-9._]*$/.test(value.trim())
    ? undefined
    : 'Please use only letters, numbers, periods, or underscores in your Instagram handle. Do not include the "@" symbol.';

const xSocialHandleValidator = (value: string) =>
  !value || /^[a-z\d_]{1,15}$/i.test(value.trim())
    ? undefined
    : 'Please use only letters, numbers, or underscores in your 𝕏 handle. Do not include the "@" symbol.';

const NCESMask = createTextMask({
  pattern: '999999999999',
  stripMask: true,
});

const einMask = createTextMask({
  pattern: '99-9999999',
  stripMask: true,
});

type FormData = Pick<
  Organization,
  | 'locationObject'
  | 'location'
  | 'ein'
  | 'brief'
  | 'categories'
  | 'SDGs'
  | 'pillars'
  | 'ERGs'
  | 'facebookPage'
  | 'instagram'
  | 'xSocial'
> & {
  NCESId: string;
  manualEntry: string;
};

const valueSelector: <K extends keyof FormData>(state: State, key: K) => FormData[K] =
  formValueSelector(FORM_IDENTIFIER);

const PresenceFormSection = ({ submitting }: { submitting: boolean }) => {
  const styles = useStyles();
  const user = useSelector(selectUser);
  const newAddress = useSelector(selectNewAddress);
  const organization = useSelector((state: State) => selectOrganization(state, true));
  const categories = useSelector((state: State) => selectCategories(state, true));
  const SDGs = useSelector((state: State) => selectSDGs(state, true));
  const pillars = useSelector((state: State) => selectPillars(state, true));
  const ERGs = useSelector((state: State) => selectERGs(state, true));
  const location = useSelector((state: State) => valueSelector(state, 'location')) || '';
  const manualEntry = useSelector((state: State) => valueSelector(state, 'manualEntry'));
  const registeredFields = useSelector((state: State) =>
    state.getIn(['form', FORM_IDENTIFIER, 'registeredFields'])
  );

  const dispatch = useDispatch();

  const isCompany = organization?.isCompany();
  const isAdmin = user?.isAdmin;

  const identifiers = organization?.identifiers || [];
  const ncesIdentifier = identifiers.find((i) => i.type === 'nces');
  const initialValues = {
    ...(organization?.toJS() || {}),
    NCESId: ncesIdentifier ? ncesIdentifier.id : undefined,
    manualEntry: organization?.locationObject?.manualEntry,
  };

  useEffect(() => {
    dispatch(initialize(FORM_IDENTIFIER, initialValues));
  }, []);

  const currentFormValues = useSelector(getFormValues(FORM_IDENTIFIER)) as TypedMap<FormData>;
  const syncErrors = useSelector(getFormSyncErrors(FORM_IDENTIFIER));

  const submit = (data = currentFormValues.toJS()) => {
    const firstSyncError = Object.values(syncErrors)[0];

    if (firstSyncError) {
      dispatch(showErrorAction(firstSyncError));
    } else {
      const formKeys = registeredFields ? [...registeredFields?.keys()] : [];
      dispatch(Actions.saveAction(_.pick(data, ['id', ...formKeys]), FORM_IDENTIFIER));
    }
  };

  const nonprofitEntityType = organization?.nonprofitEntityType;

  const isSchool = nonprofitEntityType === 'school';

  const notUS = organization?.locationObject?.countryCode
    ? organization?.locationObject?.countryCode !== countries.US.alpha2
    : !isAddressValidator(location) &&
      !location.includes('USA') &&
      !location.includes('United States');

  const showEinField = !isCompany && (!notUS || identifiers?.includes('ein'));
  const showNCESId = identifiers?.includes('nces') || (isSchool && !notUS);

  const handleFormEvent: FormEventHandler = (event) => {
    event.preventDefault();
    submit();
  };

  const pendingAddressChangeRequest = user?.additionalData
    ?.get('nonprofitAddressChangeRequests')
    ?.find(
      (request) =>
        request.status === NonprofitAddressChangeRequestStatus.Pending &&
        request.nonprofit === user.organization.id
    );

  return (
    <>
      <form noValidate onSubmitCapture={handleFormEvent}>
        <fieldset>
          <Box style={{ paddingTop: 0, marginBottom: 30 }}>
            <h2>Your Presence</h2>
            <Grid container spacing={3}>
              <Grid item sm={12} lg={12 - (showEinField ? 1 : 0) * 3 - (showNCESId ? 1 : 0) * 3}>
                {manualEntry ? (
                  <Field
                    id="locationObject"
                    name="locationObject"
                    component={FormAddressValidationField}
                    label="Location"
                    helperText="Please enter a full address by hand"
                    validate={[requiredValidator, isAddressValidator]}
                    setFieldError={(errorMessage: string) =>
                      dispatch(stopSubmit(FORM_IDENTIFIER, { locationObject: errorMessage }))
                    }
                    disabled={Boolean(pendingAddressChangeRequest)}
                  />
                ) : (
                  <Field
                    id="location"
                    name="location"
                    component={FormLocationField}
                    fullWidth
                    required
                    autoComplete="off"
                    label="Location"
                    helperText="Please enter a full address"
                    validate={[requiredValidator, isAddressValidator]}
                    alwaysDisplayErrors
                    disabled={Boolean(pendingAddressChangeRequest)}
                    sx={{ textTransform: 'uppercase' }}
                  />
                )}
                {user?.hasPrivilege(Privileges.ADDRESS_MANUAL_ENTRY) && (
                  <MUIBox mb={2}>
                    <Field
                      id="manualEntry"
                      name="manualEntry"
                      component={FormCheckbox}
                      label="Enter address manually (including PO Boxes)"
                      onChange={(value: boolean): void => {
                        if (!value) {
                          dispatch(stopSubmit(FORM_IDENTIFIER, { locationObject: undefined }));
                        }
                      }}
                      disabled={Boolean(pendingAddressChangeRequest)}
                    />
                  </MUIBox>
                )}

                {pendingAddressChangeRequest && (
                  <MUIBox mt={1}>
                    <Alert
                      icon={<InfoOutlined fontSize="inherit" />}
                      severity="info"
                      style={{ marginBottom: '16px' }}
                      classes={styles}
                    >
                      You have requested to change the nonprofit address to:{' '}
                      {renderAddress(pendingAddressChangeRequest.newAddress)}
                    </Alert>
                  </MUIBox>
                )}
              </Grid>

              {showEinField && (
                <Grid item sm={12} lg={3}>
                  <>
                    <Field
                      id="ein"
                      name="ein"
                      component={FormTextField}
                      disabled
                      fullWidth
                      label="EIN"
                      helperText="Employer Identification Number (EIN). If this is wrong please reach out to Nonprofit Support."
                      {...einMask}
                    />
                  </>
                </Grid>
              )}
              {showNCESId && (
                <Grid item sm={12} lg={3}>
                  <>
                    <Field
                      id="NCESId"
                      name="NCESId"
                      component={FormTextField}
                      disabled
                      fullWidth
                      label="NCES ID"
                      helperText="National Center for Education Statistics Identification Number. If this is wrong please reach out to Nonprofit Support."
                      {...NCESMask}
                    />
                  </>
                </Grid>
              )}
            </Grid>
            <Field
              id="brief"
              name="brief"
              component={FormTextField}
              inputProps={{ maxLength: 2000 }}
              multiline
              fullWidth
              required
              label="Mission statement"
              helperText="Please briefly describe your organization’s goals"
              validate={requiredValidator}
              alwaysDisplayErrors
            />
            <br />
            {!isCompany ? (
              <>
                <FormFieldLabel>Fields of purpose</FormFieldLabel>
                <div>
                  <FieldArray
                    name="categories"
                    component={FormValueSelector}
                    selectMultiple
                    options={categories}
                    labelText="Which category best describes your organization? (you may choose more than one, but no more than five) *"
                    validate={[notEmptyValidator, maxLength5Validator]}
                    alwaysDisplayErrors
                    maxAmountAllowed={5}
                  />
                </div>
                <br />
                <div>
                  <FieldArray
                    name="SDGs"
                    component={FormValueSelector}
                    selectMultiple
                    options={SDGs}
                    labelText="Which UN SDGs best fit your mission? (you may choose more than one, but no more than five) *"
                    validate={[notEmptyValidator, maxLength5Validator]}
                    alwaysDisplayErrors
                    maxAmountAllowed={5}
                  />
                </div>
                <br />
              </>
            ) : null}
            {isAdmin && !isCompany && (pillars.size || ERGs.size) ? (
              <>
                <FormFieldLabel>Partner pillars &amp; ERGs (Optional)</FormFieldLabel>
                {pillars.size > 0 && (
                  <div>
                    <FieldArray
                      name="pillars"
                      component={FormValueSelector}
                      selectMultiple
                      options={pillars}
                      labelText="Which partner Cause Areas match your organization?"
                    />
                  </div>
                )}
                {ERGs.size > 0 && (
                  <>
                    <br />
                    <div>
                      <FieldArray
                        name="ERGs"
                        component={FormValueSelector}
                        selectMultiple
                        options={ERGs}
                        labelText="Which partner ERGs match your organization?"
                      />
                    </div>
                  </>
                )}
              </>
            ) : null}
            <br />
            <FormFieldLabel>Social (Optional)</FormFieldLabel>
            <Grid container spacing={3}>
              <Grid item sm={12} md={4}>
                <Field
                  id="facebookPage"
                  name="facebookPage"
                  component={FormTextField}
                  inputProps={{ maxLength: 150 }}
                  fullWidth
                  autoComplete="off"
                  label="Facebook Page"
                  helperText="Facebook Page URL"
                  validate={facebookUrlValidator}
                  alwaysDisplayErrors
                />
              </Grid>
              <Grid item sm={12} md={4}>
                <Field
                  id="instagram"
                  name="instagram"
                  component={FormTextField}
                  inputProps={{ maxLength: 150 }}
                  fullWidth
                  autoComplete="off"
                  label="Instagram"
                  helperText="Instagram handle"
                  validate={instagramHandleValidator}
                  alwaysDisplayErrors
                />
              </Grid>
              <Grid item sm={12} md={4}>
                <Field
                  id="xSocial"
                  name="xSocial"
                  component={FormTextField}
                  inputProps={{ maxLength: 150 }}
                  fullWidth
                  autoComplete="off"
                  label="𝕏 (formerly Twitter)"
                  helperText="X (formerly Twitter) handle"
                  validate={xSocialHandleValidator}
                  alwaysDisplayErrors
                />
              </Grid>
            </Grid>
            <Typography align="right" style={{ marginTop: 30 }}>
              <Button type="submit" variant="contained" color="primary" disabled={submitting}>
                Submit
              </Button>
            </Typography>
          </Box>
        </fieldset>
      </form>
      {newAddress && <RequestNonprofitAddressChange newAddress={newAddress} />}
    </>
  );
};

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default withForm(PresenceFormSection);
